import React from "react";
import { TextField } from "@origin-digital/ods-core";
import { TextFieldWrapperProps } from "./types";
import { useTextFieldState } from "./useTextFieldState";
import { HelperTextNode } from "./HelperTextNode";

export const TextFieldWrapper = (props: TextFieldWrapperProps) => {
  const {
    validator,
    setValue,
    transform,
    helperText,
    value,
    onBlur,
    onChange,
    showCharacterCount,
    maxLength,
    id,
    endIcon,
    ...rest
  } = props;

  // We always show a helptext even if its blank .. so spacing between fields are even when stacked.
  const fieldState = useTextFieldState(value, showCharacterCount);

  React.useEffect(() => {
    if (!fieldState.changed && value) {
      validate(value as string);
    }
  }, [value]);

  const validate = (newValue: string) => {
    fieldState.setStartValidatingOnChange(true);
    fieldState.setError(validator(newValue), newValue);
  };

  const _onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    fieldState.onBlur();
    transformAndUpdateValue(event.target.value);
    if (onBlur) {
      onBlur(event);
    }
  };

  const _onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    transformAndUpdateValue(
      event.target.value,
      !fieldState.startValidatingOnChange
    );
    if (onChange) {
      onChange(event);
    }
  };

  const transformAndUpdateValue = (
    newValue: string,
    skipValidate: boolean = false
  ) => {
    const updatedValue = transform
      ? transform(newValue, value as string)
      : newValue;
    fieldState.onChange(updatedValue);
    if (!skipValidate) {
      validate(updatedValue);
    }
    setValue(updatedValue);
  };

  const getEndIcon = () => {
    if (!fieldState.focus) {
      if (fieldState.error) {
        return "error";
      } else if (value) {
        return endIcon ? endIcon : "success";
      }
    }
    return false;
  };

  React.useEffect(() => {
    if (!fieldState.focus) {
      if (fieldState.error) {
        props.onDeny?.(fieldState.error, value as string | undefined);
      } else if (value) {
        props.onAccept?.(value as string);
      }
    }
  }, [fieldState.focus, fieldState.error, value]);

  return (
    <TextField
      {...rest}
      id={id}
      value={value}
      helperText={
        <HelperTextNode
          {...fieldState.helperTextNodeProps}
          message={helperText || fieldState.helperTextNodeProps.message}
          maxLength={maxLength}
        />
      }
      maxLength={maxLength}
      error={rest.error || !!fieldState.error}
      onBlur={_onBlur}
      onChange={_onChange}
      onFocus={fieldState.onFocus}
      endIcon={getEndIcon()}
    />
  );
};
